import React, { useState, useEffect, useCallback } from 'react';
import { readBlobError, triggerBlobDownload } from '../utils/exportHelpers';
import {
  PageHeader, Card, Table, Field, inputStyle,
  btnPrimary, btnSm, Empty, Loading, fmtKES,
} from '../components/UI';
import axiosClient, * as api from '../api/client';
import { getBrandingSlug } from '../utils/formatters';

// ─── Color palette ────────────────────────────────────────────────────────────
const SECTOR_COLORS = {
  financial:   '#c8a96e',
  rooms:       '#3b82f6',
  fnb:         '#e67e22',
  hr:          '#8b5cf6',
  stock:       '#14b8a6',
  maintenance: '#f43f5e',
  guestsales:  '#22c55e',
  security:    '#64748b',
};

const REPORT_TYPES = [
  {
    id: 'financial',
    label: 'Financial Overview',
    icon: '💰',
    color: SECTOR_COLORS.financial,
    desc: 'Revenue, expenses, P&L, departmental breakdown',
    sectors: ['Revenue', 'Expenses', 'Profit/Loss', 'Dept Breakdown', 'Daily Trend'],
  },
  {
    id: 'rooms',
    label: 'Rooms & Occupancy',
    icon: '🏨',
    color: SECTOR_COLORS.rooms,
    desc: 'Occupancy rates, ADR, RevPAR, room status',
    sectors: ['Occupancy', 'ADR', 'RevPAR', 'Room Status', 'Housekeeping'],
  },
  {
    id: 'fnb',
    label: 'Food & Beverage',
    icon: '🍽️',
    color: SECTOR_COLORS.fnb,
    desc: 'Restaurant, bar, waiter performance, gift shop',
    sectors: ['Restaurant', 'Bar', 'Waiters', 'Gift Shop', 'Food Cost'],
  },
  {
    id: 'hr',
    label: 'HR & Payroll',
    icon: '👥',
    color: SECTOR_COLORS.hr,
    desc: 'Attendance, payroll, leave, staff performance',
    sectors: ['Attendance', 'Payroll', 'Leave', 'Staff Activity'],
  },
  {
    id: 'stock',
    label: 'Stock & Procurement',
    icon: '🏪',
    color: SECTOR_COLORS.stock,
    desc: 'Inventory levels, purchases, issuances, spoilage',
    sectors: ['Stock Levels', 'Purchases', 'Issuances', 'Spoilage'],
  },
  {
    id: 'guestsales',
    label: 'Guest & Reservations',
    icon: '🛎️',
    color: SECTOR_COLORS.guestsales,
    desc: 'Guest history, reservations, CRM, reviews',
    sectors: ['Guests', 'Reservations', 'Conference', 'Transport', 'Reviews'],
  },
  {
    id: 'security',
    label: 'Security & Gate',
    icon: '🔐',
    color: SECTOR_COLORS.security,
    desc: 'Visitor log, gate entries, vehicle tracking',
    sectors: ['Visitor Log', 'Vehicles', 'Staff Entry', 'Time on Premise'],
  },
  {
    id: 'maintenance',
    label: 'Maintenance',
    icon: '🔧',
    color: SECTOR_COLORS.maintenance,
    desc: 'Work orders, preventive maintenance, costs',
    sectors: ['Work Orders', 'PM Schedules', 'Costs', 'Asset Register'],
  },
];

const PERIODS = [
  { id: 'today',  label: 'Today' },
  { id: 'week',   label: 'This Week' },
  { id: 'month',  label: 'This Month' },
  { id: 'year',   label: 'This Year' },
  { id: 'custom', label: 'Custom Range' },
];

// ─── Tiny sparkline ───────────────────────────────────────────────────────────
function Spark({ data, color = '#c8a96e', height = 28 }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const w = data.length * 12;
  const pts = data.map((v, i) => [i * 12 + 4, height - 4 - (v / max) * (height - 8)]);
  const line = pts.map(([x, y]) => `${x},${y}`).join(' ');
  const area = `M${pts[0][0]},${height} L${pts.map(([x, y]) => `${x},${y}`).join(' L ')} L${pts[pts.length - 1][0]},${height} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${height}`} style={{ width: w, height, display: 'block' }}>
      <path d={area} fill={color + '28'} />
      <polyline points={line} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Progress bar row ─────────────────────────────────────────────────────────
function BarRow({ label, value, max, color, fmt }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 12 }}>
        <span style={{ color: 'var(--textSecond)' }}>{label}</span>
        <span style={{ fontWeight: 700, color: 'var(--textPrimary)' }}>{fmt ? fmt(value) : value}</span>
      </div>
      <div style={{ height: 6, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 99, transition: 'width .6s ease' }} />
      </div>
    </div>
  );
}

// ─── KPI tile ─────────────────────────────────────────────────────────────────
function KpiTile({ label, value, icon, sub, color, spark }) {
  return (
    <div style={{
      background: 'var(--bgCard)', border: `1px solid ${color}33`,
      borderLeft: `3px solid ${color}`, borderRadius: 10, padding: '14px 16px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
        <span style={{ fontSize: 16 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color, letterSpacing: -0.5, marginBottom: 4 }}>{value}</div>
      {spark && <Spark data={spark} color={color} />}
      {sub && <div style={{ fontSize: 10, color: 'var(--textFaint)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function ReportingPage({ notify }) {
  const [activeReport, setActiveReport] = useState(null);
  const [period, setPeriod]   = useState('month');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');

  return (
    <div>
      <PageHeader
        title="Reports & Analytics"
        sub="Cross-sector reporting hub — generate insights from every department for informed decision-making"
      />

      {!activeReport ? (
        <ReportSelector onSelect={setActiveReport} />
      ) : (
        <ReportViewer
          report={activeReport}
          period={period}
          setPeriod={setPeriod}
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          dateTo={dateTo}
          setDateTo={setDateTo}
          onBack={() => setActiveReport(null)}
          notify={notify}
        />
      )}
    </div>
  );
}

// ─── Report selector grid ─────────────────────────────────────────────────────
function ReportSelector({ onSelect }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--textMuted)', marginBottom: 20, padding: '10px 14px', background: 'var(--bgHover)', border: '1px solid var(--border)', borderRadius: 6 }}>
        📊 Select a sector to generate a report. All reports support custom date ranges and can be exported to Excel or PDF.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
        {REPORT_TYPES.map(rt => (
          <button key={rt.id} onClick={() => onSelect(rt)} style={{
            background: 'var(--bgCard)', border: `1px solid var(--border)`,
            borderRadius: 12, padding: '18px 20px', cursor: 'pointer',
            textAlign: 'left', fontFamily: 'inherit',
            transition: 'all .15s', position: 'relative', overflow: 'hidden',
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = rt.color + '88'; e.currentTarget.style.boxShadow = `0 4px 24px ${rt.color}22`; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            {/* Accent left bar */}
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: rt.color, borderRadius: '12px 0 0 12px' }} />
            {/* Ghost icon */}
            <div style={{ position: 'absolute', top: -10, right: -8, fontSize: 52, opacity: 0.06, pointerEvents: 'none' }}>{rt.icon}</div>

            <div style={{ paddingLeft: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 22 }}>{rt.icon}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--textPrimary)' }}>{rt.label}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--textMuted)', marginBottom: 10, lineHeight: 1.5 }}>{rt.desc}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {rt.sectors.map(s => (
                  <span key={s} style={{ fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 3, background: rt.color + '18', color: rt.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s}</span>
                ))}
              </div>
              <div style={{ marginTop: 12, fontSize: 10, color: rt.color, fontWeight: 800 }}>Generate Report →</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Report viewer (wraps all individual report panels) ──────────────────────
function ReportViewer({ report, period, setPeriod, dateFrom, setDateFrom, dateTo, setDateTo, onBack, notify }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);

  // Reset data whenever the report type changes
  useEffect(() => {
    setData(null);
    setErr(null);
  }, [report.id]);

  const load = useCallback(async () => {
    setErr(null);
    if (period === 'custom' && (!dateFrom || !dateTo)) {
      setErr('Select both a start and end date for the custom range.');
      return;
    }
    setLoading(true);
    setData(null);
    try {
      const params = period === 'custom'
        ? `?period=custom&date_from=${dateFrom}&date_to=${dateTo}`
        : `?period=${period}`;
      const result = await fetchReportData(report.id, params);
      setData(result);
    } catch (e) {
      setErr(e.message || 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [report.id, period, dateFrom, dateTo]);

  useEffect(() => {
    if (period !== 'custom') load();
  }, [period, report.id]); // eslint-disable-line

  // ── Export: only financial report uses the accountant export endpoint.
  // All other reports show a notice that detailed export is via the financial module.
  const doExport = async (type) => {
    if (report.id !== 'financial') {
      notify(`For full ${type.toUpperCase()} exports, use the Financial Overview report which covers all departments.`, 'error');
      return;
    }
    notify(`Preparing ${report.label} ${type.toUpperCase()} export…`);
    try {
      const params = period === 'custom' && dateFrom && dateTo
        ? `?period=custom&date_from=${dateFrom}&date_to=${dateTo}`
        : `?period=${period}`;
      const endpoint = type === 'pdf'
        ? `/accountant/export/pdf/${params}`
        : `/accountant/export/excel/${params}`;
      // Use axios (already has Authorization header set) instead of native fetch
      // to avoid cross-origin auth issues between Netlify and Render
      // baseURL:'' ensures the absolute endpoint path is used correctly
      const r = await axiosClient.get(endpoint, { responseType: 'blob', baseURL: '', timeout: 120000 });
      // r.data is already a Blob when responseType:'blob' — do not re-wrap with new Blob([])
      const blob = r.data;
      if (blob.size < 100) throw new Error('Empty file returned');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const dateStr = period === 'custom' ? `${dateFrom}_to_${dateTo}` : period;
      a.download = `${getBrandingSlug()}_${report.id}_${dateStr}.${type === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(a.href); }, 1000);
      notify(`${report.label} report exported ✓`);
    } catch (e) {
      notify(`Export failed: ${await readBlobError(e)}`, 'error');
    }
  };

  return (
    <div>
      {/* Header bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ ...btnSm, background: 'var(--bgInput)', color: 'var(--textMuted)' }}>← All Reports</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <span style={{ fontSize: 20 }}>{report.icon}</span>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: report.color }}>{report.label}</div>
            <div style={{ fontSize: 11, color: 'var(--textFaint)' }}>{report.desc}</div>
          </div>
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={period} onChange={e => setPeriod(e.target.value)} style={{
            padding: '6px 10px', borderRadius: 7, border: `1.5px solid ${report.color}55`,
            background: 'var(--bgCard)', color: 'var(--textPrimary)', fontSize: 12.5,
            fontFamily: 'inherit', cursor: 'pointer', minWidth: 130,
          }}>
            {PERIODS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          {report.id === 'financial' && <>
            <button onClick={() => doExport('excel')} style={{ ...btnSm, border: `1px solid ${report.color}66`, color: report.color }}>⬇ Excel</button>
            <button onClick={() => doExport('pdf')} style={{ ...btnSm, background: report.color + '18', border: `1px solid ${report.color}66`, color: report.color }}>⬇ PDF</button>
          </>}
        </div>
      </div>

      {/* Sector pills — show what this report covers */}
      {report.sectors && report.sectors.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14, padding: '10px 14px', background: 'var(--bgHover)', border: '1px solid var(--border)', borderRadius: 8 }}>
          <span style={{ fontSize: 10, color: 'var(--textFaint)', marginRight: 4, alignSelf: 'center', textTransform: 'uppercase', letterSpacing: 1 }}>Covers:</span>
          {report.sectors.map(s => (
            <span key={s} style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 4, background: report.color + '18', color: report.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s}</span>
          ))}
        </div>
      )}

      {/* Custom range picker */}
      {period === 'custom' && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', padding: '12px 16px', background: 'var(--bgCard)', border: '1px solid var(--borderMid)', borderRadius: 8, marginBottom: 16 }}>
          <Field label="From Date"><input type="date" style={inputStyle} value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></Field>
          <Field label="To Date"><input type="date" style={inputStyle} value={dateTo} onChange={e => setDateTo(e.target.value)} /></Field>
          <button onClick={load} style={{ ...btnPrimary, marginTop: 0, minWidth: 130 }} disabled={!dateFrom || !dateTo}>Generate</button>
        </div>
      )}

      {err && !data && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 28, opacity: .3, marginBottom: 10 }}>⚠</div>
          <div style={{ color: 'var(--textMuted)', marginBottom: 14 }}>{err}</div>
          {period !== 'custom' && <button onClick={load} className="btn btn-primary">Retry</button>}
        </div>
      )}
      {loading && <Loading />}

      {/* Render the right panel */}
      {!loading && data && (
        <>
          {report.id === 'financial'   && <FinancialReport   data={data} period={period} color={report.color} />}
          {report.id === 'rooms'       && <RoomsReport       data={data} color={report.color} />}
          {report.id === 'fnb'         && <FnBReport         data={data} color={report.color} />}
          {report.id === 'hr'          && <HRReport          data={data} color={report.color} />}
          {report.id === 'stock'       && <StockReport       data={data} color={report.color} />}
          {report.id === 'guestsales'  && <GuestSalesReport  data={data} color={report.color} />}
          {report.id === 'security'    && <SecurityReport    data={data} color={report.color} />}
          {report.id === 'maintenance' && <MaintenanceReport data={data} color={report.color} />}
        </>
      )}
    </div>
  );
}

// ─── Data fetcher (orchestrates multiple API calls per report type) ────────────
async function fetchReportData(reportId, params) {
  switch (reportId) {
    case 'financial': {
      const [acc, pay, exp] = await Promise.allSettled([
        api.getAccountantReports(params),
        api.getPayments(),
        api.getExpenses(),
      ]);
      const d = acc.status === 'fulfilled' ? acc.value.data : {};
      return {
        revenue:      d.revenue       || {},
        expenses:     d.expenses      || {},
        rooms:        d.rooms         || {},
        waiter_stats: d.waiter_stats  || [],
        daily_trend:  d.daily_trend   || [],
        period:       d.period        || {},
        payments:     pay.status === 'fulfilled' ? (pay.value.data.results || pay.value.data || []) : [],
        expense_list: exp.status === 'fulfilled' ? (exp.value.data.results || exp.value.data || []) : [],
      };
    }
    case 'rooms': {
      const [rooms, guests, hk] = await Promise.allSettled([
        api.getRooms(),
        api.getGuests(),
        api.getHousekeepingLogs(),
      ]);
      return {
        rooms:    rooms.status   === 'fulfilled' ? (rooms.value.data.results || rooms.value.data || []) : [],
        guests:   guests.status  === 'fulfilled' ? (guests.value.data.results || guests.value.data || []) : [],
        hk_logs:  hk.status      === 'fulfilled' ? (hk.value.data.results || hk.value.data || []) : [],
      };
    }
    case 'fnb': {
      const [rpt, gift, orders] = await Promise.allSettled([
        api.getManagerReports(params),
        api.getGiftShopSummary(),
        api.getOrders(),
      ]);
      return {
        manager:  rpt.status   === 'fulfilled' ? rpt.value.data   : {},
        gift:     gift.status  === 'fulfilled' ? gift.value.data  : {},
        orders:   orders.status === 'fulfilled' ? (orders.value.data.results || orders.value.data || []) : [],
      };
    }
    case 'hr': {
      const [users, leave, monthly] = await Promise.allSettled([
        api.getUsers(),
        api.getLeaveRequests(),
        api.getMonthlyAttendance(''),
      ]);
      return {
        users:   users.status   === 'fulfilled' ? (users.value.data.results || users.value.data || []) : [],
        leave:   leave.status   === 'fulfilled' ? (leave.value.data.results || leave.value.data || []) : [],
        monthly: monthly.status === 'fulfilled' ? monthly.value.data : {},
      };
    }
    case 'stock': {
      const [stock, iss, purch, spoil] = await Promise.allSettled([
        api.getStockItems(),
        api.getIssuances(),
        api.getPurchases(),
        api.getSpoilageSummary(),
      ]);
      return {
        stock:   stock.status  === 'fulfilled' ? (stock.value.data.results || stock.value.data || []) : [],
        iss:     iss.status    === 'fulfilled' ? (iss.value.data.results || iss.value.data || []) : [],
        purch:   purch.status  === 'fulfilled' ? (purch.value.data.results || purch.value.data || []) : [],
        spoil:   spoil.status  === 'fulfilled' ? spoil.value.data : {},
      };
    }
    case 'guestsales': {
      const [guests, res, conf, transport, reviews] = await Promise.allSettled([
        api.getGuests(),
        api.getReservations(),
        api.getConferenceBookings(),
        api.getTransportSummary(),
        api.getFeedback(),
      ]);
      return {
        guests:    guests.status    === 'fulfilled' ? (guests.value.data.results || guests.value.data || []) : [],
        res:       res.status       === 'fulfilled' ? (res.value.data.results || res.value.data || []) : [],
        conf:      conf.status      === 'fulfilled' ? (conf.value.data.results || conf.value.data || []) : [],
        transport: transport.status === 'fulfilled' ? transport.value.data : {},
        reviews:   reviews.status   === 'fulfilled' ? (reviews.value.data || []) : [],
      };
    }
    case 'security': {
      const [gate] = await Promise.allSettled([api.getGateVisitors()]);
      return {
        visitors: gate.status === 'fulfilled' ? (gate.value.data.results || gate.value.data || []) : [],
      };
    }
    case 'maintenance': {
      const [wo, summary, assets] = await Promise.allSettled([
        api.getWorkOrders(),
        api.getMaintenanceSummary(),
        api.getAssets(),
      ]);
      return {
        work_orders: wo.status      === 'fulfilled' ? (wo.value.data.results || wo.value.data || []) : [],
        summary:     summary.status === 'fulfilled' ? summary.value.data : {},
        assets:      assets.status  === 'fulfilled' ? (assets.value.data.results || assets.value.data || []) : [],
      };
    }
    default:
      return {};
  }
}

// ════════════════════════════════════════════════════════════════════════════════
// INDIVIDUAL REPORT PANELS
// ════════════════════════════════════════════════════════════════════════════════

function FinancialReport({ data, color }) {
  const rev = data.revenue || {};
  const exp = data.expenses || {};
  const rooms = data.rooms || {};
  const grand = rev.total || 0;
  const depts = [
    ['Rooms',       rev.room          || 0, '#2563eb'],
    ['Restaurant',  rev.food          || 0, '#c8a96e'],
    ['Bar',         rev.bar           || 0, '#7c3aed'],
    ['Pool',        rev.pool          || 0, '#0891b2'],
    ['Conference',  rev.conference    || 0, '#ea580c'],
    ['Gift Shop',   rev.gift_shop     || 0, '#059669'],
    ['Donations',   rev.donations     || 0, '#16a34a'],
  ].filter(([, v]) => v > 0);

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        {[
          { label: 'Total Revenue',  value: fmtKES(grand),               icon: '💰', color },
          { label: 'Net Revenue',    value: fmtKES(rev.net_ex_vat || 0), icon: '📈', color: '#22c55e' },
          { label: 'Total Expenses', value: fmtKES(exp.total || 0),      icon: '📉', color: '#ef4444' },
          { label: 'Net Profit',     value: fmtKES(exp.net_profit || 0), icon: '💎', color: exp.net_profit >= 0 ? '#22c55e' : '#ef4444' },
        ].map((k, i) => <KpiTile key={i} {...k} />)}
      </div>

      {/* Revenue breakdown + Expense breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Card title="Revenue by Department">
          {depts.map(([lbl, amt, col]) => (
            <BarRow key={lbl} label={lbl} value={amt} max={grand} color={col} fmt={fmtKES} />
          ))}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ fontWeight: 700 }}>Grand Total</span>
            <span style={{ fontWeight: 800, color }}>{fmtKES(grand)}</span>
          </div>
        </Card>
        <Card title="Expense Breakdown">
          {(exp.by_category || []).length === 0 ? <Empty text="No expenses recorded" /> :
            (exp.by_category || []).map((cat, i) => {
              const cols = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899'];
              return <BarRow key={i} label={cat.label || cat.category} value={cat.amount} max={exp.total || 1} color={cols[i % cols.length]} fmt={fmtKES} />;
            })
          }
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ fontWeight: 700 }}>Total</span>
            <span style={{ fontWeight: 800, color: '#ef4444' }}>{fmtKES(exp.total || 0)}</span>
          </div>
        </Card>
      </div>

      {/* P&L Summary */}
      <Card title="Profit & Loss Summary">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
          {[
            { label: 'Gross Revenue',          value: fmtKES(grand),                      color: 'var(--textPrimary)' },
            { label: 'Less: VAT (16%)',         value: `(${fmtKES(rev.vat_collected || 0)})`, color: '#ef4444' },
            { label: 'Less: Catering Levy (2%)',value: `(${fmtKES(rev.catering_levy || 0)})`, color: '#e67e22' },
            { label: 'Net Revenue',             value: fmtKES(rev.net_ex_vat || 0),        color },
            { label: 'Total Expenses',          value: `(${fmtKES(exp.total || 0)})`,      color: '#ef4444' },
            { label: 'Net Profit/(Loss)',        value: fmtKES(exp.net_profit || 0),        color: (exp.net_profit || 0) >= 0 ? '#22c55e' : '#ef4444' },
          ].map(item => (
            <div key={item.label} style={{ padding: '10px 14px', background: 'var(--bgInput)', borderRadius: 8, border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 10, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 5 }}>{item.label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: item.color }}>{item.value}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Waiter performance */}
      {(data.waiter_stats || []).length > 0 && (
        <Card title="Staff Performance">
          <Table
            headers={['Staff', 'Orders', 'Revenue', 'Pending']}
            rows={(data.waiter_stats || []).map(w => [
              <span style={{ fontWeight: 600 }}>{w.name}</span>,
              w.orders,
              <span style={{ fontWeight: 700, color }}>{fmtKES(w.revenue)}</span>,
              <span style={{ color: w.pending > 0 ? '#f59e0b' : '#22c55e' }}>{w.pending}</span>,
            ])}
          />
        </Card>
      )}

      {/* Daily trend */}
      {(data.daily_trend || []).length > 0 && (
        <Card title={`Daily Breakdown (${data.daily_trend.length} days)`}>
          <div style={{ overflowX: 'auto', maxHeight: 300, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>{['Date', 'Revenue', 'Expenses', 'Net'].map(h => (
                  <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontSize: 10, color: 'var(--textFaint)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, borderBottom: '2px solid var(--border)' }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {data.daily_trend.map((day, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontSize: 11 }}>{day.date}</td>
                    <td style={{ padding: '7px 10px', color: '#22c55e', fontWeight: 600 }}>{fmtKES(day.revenue)}</td>
                    <td style={{ padding: '7px 10px', color: '#ef4444' }}>{fmtKES(day.expense)}</td>
                    <td style={{ padding: '7px 10px', fontWeight: 700, color: (day.revenue - day.expense) >= 0 ? '#22c55e' : '#ef4444' }}>{fmtKES(day.revenue - day.expense)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function RoomsReport({ data, color }) {
  const rooms    = data.rooms    || [];
  const guests   = data.guests   || [];
  const hk_logs  = data.hk_logs  || [];

  const occupied  = rooms.filter(r => r.status === 'occupied').length;
  const available = rooms.filter(r => r.status === 'available').length;
  const ooo       = rooms.filter(r => r.status === 'maintenance').length;
  const total     = rooms.length;
  const occPct    = total > 0 ? Math.round(occupied / total * 100) : 0;

  const hkCount   = { clean: 0, dirty: 0, inspecting: 0, 'out-of-order': 0 };
  rooms.forEach(r => { if (hkCount[r.hk_status] !== undefined) hkCount[r.hk_status]++; });

  const typeGroups = {};
  rooms.forEach(r => { if (!typeGroups[r.type]) typeGroups[r.type] = { total: 0, occupied: 0, revenue: 0 }; typeGroups[r.type].total++; if (r.status === 'occupied') typeGroups[r.type].occupied++; });

  const checkedInToday = guests.filter(g => g.check_in === new Date().toISOString().slice(0, 10)).length;
  const checkoutToday  = guests.filter(g => g.check_out === new Date().toISOString().slice(0, 10)).length;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
        {[
          { label: 'Total Rooms',  value: total,    icon: '🏨', color },
          { label: 'Occupied',     value: occupied, icon: '🔴', color: '#ef4444' },
          { label: 'Available',    value: available,icon: '🟢', color: '#22c55e' },
          { label: 'Maintenance',  value: ooo,      icon: '🔧', color: '#f59e0b' },
          { label: 'Occupancy %',  value: `${occPct}%`, icon: '📊', color },
          { label: 'Check-ins Today', value: checkedInToday, icon: '⬇️', color: '#3b82f6' },
          { label: 'Checkouts Today', value: checkoutToday,  icon: '⬆️', color: '#8b5cf6' },
        ].map((k, i) => <KpiTile key={i} {...k} />)}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Card title="Room Type Breakdown">
          {Object.entries(typeGroups).map(([type, info]) => {
            const pct = info.total > 0 ? Math.round(info.occupied / info.total * 100) : 0;
            return (
              <div key={type} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 12 }}>
                  <span style={{ fontWeight: 600 }}>{type}</span>
                  <span style={{ color: 'var(--textFaint)', fontSize: 11 }}>{info.occupied}/{info.total} occupied</span>
                </div>
                <div style={{ height: 6, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 99 }} />
                </div>
                <div style={{ fontSize: 10, color: 'var(--textFaint)', marginTop: 3 }}>{pct}% occupancy</div>
              </div>
            );
          })}
        </Card>

        <Card title="Housekeeping Status">
          {Object.entries({ clean: '#22c55e', dirty: '#e67e22', inspecting: '#3b82f6', 'out-of-order': '#a855f7' }).map(([status, col]) => (
            <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: col }} />
              <span style={{ flex: 1, fontSize: 12, textTransform: 'capitalize', color: 'var(--textSecond)' }}>{status.replace('-', ' ')}</span>
              <span style={{ fontWeight: 700, color: col, fontSize: 14 }}>{hkCount[status] || 0}</span>
            </div>
          ))}
        </Card>
      </div>

      {/* Room list */}
      <Card title={`Room Status (${rooms.length} rooms)`}>
        <div style={{ overflowX: 'auto' }}>
          <Table
            headers={['Room', 'Type', 'Floor', 'Status', 'HK Status', 'Rate/Night']}
            rows={rooms.map(r => [
              <span style={{ fontWeight: 700 }}>Room {r.number}</span>,
              r.type,
              `Floor ${r.floor}`,
              <span style={{ color: r.status === 'available' ? '#22c55e' : r.status === 'occupied' ? '#ef4444' : '#f59e0b', fontWeight: 700, fontSize: 11 }}>{r.status}</span>,
              <span style={{ fontSize: 11, color: 'var(--textMuted)', textTransform: 'capitalize' }}>{(r.hk_status || '').replace('-', ' ')}</span>,
              <span style={{ color: '#c8a96e', fontWeight: 600 }}>{fmtKES(r.price)}</span>,
            ])}
          />
        </div>
      </Card>
    </div>
  );
}

function FnBReport({ data, color }) {
  const mgr    = data.manager || {};
  const orders = data.orders  || [];
  const gift   = data.gift    || {};

  const totalOrders = orders.length;
  const paidOrders  = orders.filter(o => o.status === 'paid').length;
  const pendOrders  = orders.filter(o => o.status === 'pending').length;
  const kitchenDone = orders.filter(o => o.kitchen_status === 'done').length;

  const categoryRevMap = {};
  orders.forEach(o => {
    const cat = o.order_type || 'restaurant';
    categoryRevMap[cat] = (categoryRevMap[cat] || 0) + parseFloat(o.total || 0);
  });

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
        {[
          { label: 'Total Orders',   value: totalOrders,  icon: '📋', color },
          { label: 'Paid',           value: paidOrders,   icon: '✅', color: '#22c55e' },
          { label: 'Pending',        value: pendOrders,   icon: '⏳', color: '#f59e0b' },
          { label: 'Kitchen Done',   value: kitchenDone,  icon: '👨‍🍳', color: '#e67e22' },
          { label: 'Gift Shop Items',value: gift.total_items  || 0, icon: '🛍️', color: '#8b5cf6' },
          { label: 'Gift Shop Sales',value: fmtKES(gift.total_revenue || 0), icon: '💰', color: '#14b8a6' },
        ].map((k, i) => <KpiTile key={i} {...k} />)}
      </div>

      {Object.keys(categoryRevMap).length > 0 && (
        <Card title="Revenue by Category">
          {Object.entries(categoryRevMap).map(([cat, amt]) => (
            <BarRow key={cat} label={cat.charAt(0).toUpperCase() + cat.slice(1)} value={amt}
              max={Math.max(...Object.values(categoryRevMap), 1)} color={color} fmt={fmtKES} />
          ))}
        </Card>
      )}

      {/* Waiter stats from manager reports */}
      {(mgr.waiter_stats || []).length > 0 && (
        <Card title="Waiter Performance">
          <Table
            headers={['Waiter', 'Orders', 'Revenue', 'Avg/Order']}
            rows={(mgr.waiter_stats || []).map(w => [
              <span style={{ fontWeight: 600 }}>{w.name}</span>,
              w.orders,
              <span style={{ fontWeight: 700, color }}>{fmtKES(w.revenue)}</span>,
              fmtKES(w.orders > 0 ? w.revenue / w.orders : 0),
            ])}
          />
        </Card>
      )}

      {orders.length > 0 && (
        <Card title={`Recent Orders (${Math.min(orders.length, 20)} shown)`}>
          <div style={{ overflowX: 'auto' }}>
            <Table
              headers={['Bill #', 'Table/Type', 'Waiter', 'Amount', 'Status', 'Time']}
              rows={[...orders].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 20).map(o => [
                <span style={{ fontFamily: 'monospace', color: '#c8a96e', fontSize: 11 }}>#{o.id}</span>,
                o.table_number || o.order_type || '—',
                o.waiter_name || '—',
                <span style={{ fontWeight: 700, color }}>{fmtKES(o.total)}</span>,
                <span style={{ fontSize: 10, fontWeight: 700, color: o.status === 'paid' ? '#22c55e' : '#f59e0b', textTransform: 'uppercase' }}>{o.status}</span>,
                <span style={{ fontSize: 10, color: 'var(--textFaint)' }}>{new Date(o.created_at).toLocaleString('en-KE', { dateStyle: 'short', timeStyle: 'short' })}</span>,
              ])}
            />
          </div>
        </Card>
      )}
    </div>
  );
}

function HRReport({ data, color }) {
  const users   = data.users   || [];
  const leave   = data.leave   || [];
  const monthly = data.monthly || {};

  const active   = users.filter(u => u.is_active && !u.is_terminated);
  const inactive = users.filter(u => !u.is_active && !u.is_terminated);
  const terminated = users.filter(u => u.is_terminated);

  const roleMap = {};
  active.forEach(u => { roleMap[u.role] = (roleMap[u.role] || 0) + 1; });

  const pendingLeave   = leave.filter(l => l.status === 'pending').length;
  const approvedLeave  = leave.filter(l => l.status === 'approved').length;
  const onLeave        = leave.filter(l => l.status === 'approved' && l.credentials_deactivated).length;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
        {[
          { label: 'Total Staff',   value: users.length,    icon: '👥', color },
          { label: 'Active',        value: active.length,   icon: '✅', color: '#22c55e' },
          { label: 'Inactive',      value: inactive.length, icon: '⏸️', color: '#f59e0b' },
          { label: 'Terminated',    value: terminated.length,icon: '🚫', color: '#ef4444' },
          { label: 'On Leave',      value: onLeave,          icon: '🏖️', color: '#8b5cf6' },
          { label: 'Leave Pending', value: pendingLeave,     icon: '⏳', color: '#e67e22' },
        ].map((k, i) => <KpiTile key={i} {...k} />)}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Card title="Staff by Role">
          {Object.entries(roleMap).sort((a, b) => b[1] - a[1]).map(([role, count]) => (
            <BarRow key={role} label={role.replace(/_/g, ' ')} value={count}
              max={Math.max(...Object.values(roleMap), 1)} color={color} />
          ))}
        </Card>
        <Card title="Leave Overview">
          <Table
            headers={['Employee', 'Type', 'Dates', 'Status']}
            rows={leave.slice(0, 15).map(lr => [
              lr.employee_name,
              lr.leave_type,
              `${lr.date_from} → ${lr.date_to}`,
              <span style={{ fontSize: 10, fontWeight: 700, color: lr.status === 'pending' ? '#e67e22' : lr.status === 'approved' ? '#22c55e' : '#ef4444', textTransform: 'uppercase' }}>{lr.status}</span>,
            ])}
          />
        </Card>
      </div>

      {(monthly.report || []).length > 0 && (
        <Card title={`Monthly Attendance — ${monthly.month}`}>
          <div style={{ overflowX: 'auto' }}>
            <Table
              headers={['Employee', 'Role', 'Present', 'Absent', 'Half Day', 'Late', 'Leave']}
              rows={(monthly.report || []).map(r => [
                <span style={{ fontWeight: 600 }}>{r.employee_name}</span>,
                <span style={{ fontSize: 10, color: 'var(--textMuted)', textTransform: 'capitalize' }}>{r.role?.replace(/_/g, ' ')}</span>,
                <span style={{ color: '#22c55e', fontWeight: 700 }}>{r.present}</span>,
                <span style={{ color: '#ef4444' }}>{r.absent}</span>,
                <span style={{ color: '#e67e22' }}>{r.half_day}</span>,
                <span style={{ color: '#f59e0b' }}>{r.late}</span>,
                <span style={{ color: '#3b82f6' }}>{r.on_leave}</span>,
              ])}
            />
          </div>
        </Card>
      )}
    </div>
  );
}

function StockReport({ data, color }) {
  const stock  = data.stock  || [];
  const iss    = data.iss    || [];
  const purch  = data.purch  || [];
  const spoil  = data.spoil  || {};

  const lowStock    = stock.filter(s => s.quantity <= (s.reorder_level || 10));
  const zeroStock   = stock.filter(s => s.quantity <= 0);
  const totalItems  = stock.length;

  const totalPurchValue = purch.reduce((s, p) => s + parseFloat(p.total_cost || 0), 0);
  const totalIssValue   = iss.reduce((s, i) => s + parseFloat(i.total_cost || 0), 0);

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
        {[
          { label: 'Stock Items',    value: totalItems,    icon: '📦', color },
          { label: 'Low Stock',      value: lowStock.length, icon: '⚠️', color: '#f59e0b' },
          { label: 'Out of Stock',   value: zeroStock.length,icon: '🚫', color: '#ef4444' },
          { label: 'Total Purchases',value: fmtKES(totalPurchValue), icon: '🛒', color: '#22c55e' },
          { label: 'Total Issuances',value: fmtKES(totalIssValue),   icon: '📤', color: '#8b5cf6' },
          { label: 'Spoilage Value', value: fmtKES(spoil.total_value || 0), icon: '🗑️', color: '#ef4444' },
        ].map((k, i) => <KpiTile key={i} {...k} />)}
      </div>

      {lowStock.length > 0 && (
        <Card title={`⚠️ Low Stock Alert (${lowStock.length} items)`} style={{ border: '1px solid #f59e0b44' }}>
          <Table
            headers={['Item', 'Category', 'Qty', 'Reorder Level', 'Unit']}
            rows={lowStock.map(s => [
              <span style={{ fontWeight: 600, color: '#f59e0b' }}>{s.name}</span>,
              s.category || '—',
              <span style={{ fontWeight: 800, color: s.quantity <= 0 ? '#ef4444' : '#f59e0b' }}>{s.quantity}</span>,
              s.reorder_level || 10,
              s.unit || '—',
            ])}
          />
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Card title={`Recent Purchases (${purch.length})`}>
          <Table
            headers={['Ref', 'Supplier', 'Items', 'Total', 'Status']}
            rows={purch.slice(0, 12).map(p => [
              <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#c8a96e' }}>#{p.id}</span>,
              p.supplier_name || p.vendor_name || '—',
              (p.items || []).length || '—',
              <span style={{ fontWeight: 700, color }}>{fmtKES(p.total_cost || 0)}</span>,
              <span style={{ fontSize: 10, fontWeight: 700, color: p.status === 'confirmed' ? '#22c55e' : '#f59e0b', textTransform: 'uppercase' }}>{p.status}</span>,
            ])}
          />
        </Card>
        <Card title={`Recent Issuances (${iss.length})`}>
          <Table
            headers={['Ref', 'Department', 'Items', 'Total', 'Date']}
            rows={iss.slice(0, 12).map(i => [
              <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#c8a96e' }}>#{i.id}</span>,
              i.department || i.issued_to || '—',
              (i.items || []).length || '—',
              <span style={{ fontWeight: 700, color }}>{fmtKES(i.total_cost || 0)}</span>,
              <span style={{ fontSize: 10, color: 'var(--textFaint)' }}>{new Date(i.issued_date || i.created_at || Date.now()).toLocaleDateString('en-KE')}</span>,
            ])}
          />
        </Card>
      </div>
    </div>
  );
}

function GuestSalesReport({ data, color }) {
  const guests    = data.guests    || [];
  const res       = data.res       || [];
  const conf      = data.conf      || [];
  const transport = data.transport || {};
  const reviews   = data.reviews   || [];

  const checkedIn   = guests.filter(g => g.status === 'checked-in').length;
  const checkedOut  = guests.filter(g => g.status === 'checked-out').length;
  const totalGuests = guests.length;

  const confRevenue = conf.filter(c => c.status === 'completed').reduce((s, c) => s + parseFloat(c.total || 0), 0);
  const avgRating   = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : '—';
  const fiveStars   = reviews.filter(r => r.rating === 5).length;

  const payModes = {};
  guests.forEach(g => { payModes[g.payment_mode] = (payModes[g.payment_mode] || 0) + 1; });

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
        {[
          { label: 'Total Guests',   value: totalGuests,     icon: '👤', color },
          { label: 'In-House',       value: checkedIn,       icon: '🏨', color: '#22c55e' },
          { label: 'Checked Out',    value: checkedOut,      icon: '🚪', color: '#3b82f6' },
          { label: 'Reservations',   value: res.length,      icon: '📅', color: '#8b5cf6' },
          { label: 'Conference Rev', value: fmtKES(confRevenue), icon: '🏛️', color: '#e67e22' },
          { label: 'Avg Rating',     value: `${avgRating}⭐`, icon: '⭐', color: '#f59e0b' },
          { label: '5-Star Reviews', value: fiveStars,       icon: '🌟', color: '#22c55e' },
          { label: 'Transport Rev',  value: fmtKES(transport.total_revenue || 0), icon: '🚐', color: '#14b8a6' },
        ].map((k, i) => <KpiTile key={i} {...k} />)}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Card title="Recent Guests">
          <Table
            headers={['Guest', 'Room', 'Check-in', 'Check-out', 'Status']}
            rows={guests.slice(0, 15).map(g => [
              <span style={{ fontWeight: 600 }}>{g.name}</span>,
              g.room ? `Room ${g.room}` : '—',
              g.check_in,
              g.check_out,
              <span style={{ fontSize: 10, fontWeight: 700, color: g.status === 'checked-in' ? '#22c55e' : '#3b82f6', textTransform: 'capitalize' }}>{g.status}</span>,
            ])}
          />
        </Card>
        <Card title="Payment Mode Distribution">
          {Object.entries(payModes).map(([mode, count]) => (
            <BarRow key={mode} label={mode} value={count}
              max={Math.max(...Object.values(payModes), 1)} color={color} />
          ))}
        </Card>
      </div>

      {conf.length > 0 && (
        <Card title="Conference Bookings">
          <Table
            headers={['Code', 'Organiser', 'Event', 'Attendees', 'Total', 'Status']}
            rows={conf.slice(0, 12).map(c => [
              <span style={{ fontFamily: 'monospace', color: '#c8a96e', fontSize: 11 }}>{c.confirmation_code}</span>,
              c.organiser_name,
              c.event_name,
              `${c.attendees} pax`,
              <span style={{ fontWeight: 700, color }}>{fmtKES(c.total)}</span>,
              <span style={{ fontSize: 10, fontWeight: 700, color: c.status === 'completed' ? '#22c55e' : '#f59e0b', textTransform: 'uppercase' }}>{c.status}</span>,
            ])}
          />
        </Card>
      )}

      {reviews.length > 0 && (
        <Card title={`Guest Reviews (${reviews.length})`}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 14 }}>
            {[5, 4, 3, 2, 1].map(n => {
              const cnt = reviews.filter(r => r.rating === n).length;
              const pct = reviews.length ? Math.round(cnt / reviews.length * 100) : 0;
              return (
                <div key={n} style={{ textAlign: 'center', padding: '10px 8px', background: 'var(--bgInput)', borderRadius: 6 }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#f59e0b' }}>{cnt}</div>
                  <div style={{ fontSize: 12, color: '#f59e0b' }}>{'★'.repeat(n)}</div>
                  <div style={{ fontSize: 10, color: 'var(--textFaint)', marginTop: 2 }}>{pct}%</div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

function SecurityReport({ data, color }) {
  const visitors = data.visitors || [];
  const inside   = visitors.filter(v => v.status === 'in').length;
  const exited   = visitors.filter(v => v.status === 'out').length;
  const withCar  = visitors.filter(v => v.car_plate).length;

  const typeMap = {};
  visitors.forEach(v => { typeMap[v.visitor_type] = (typeMap[v.visitor_type] || 0) + 1; });

  const avgDur = (() => {
    const ex = visitors.filter(v => v.time_in && v.time_out);
    if (!ex.length) return 0;
    return Math.round(ex.reduce((s, v) => s + Math.round((new Date(v.time_out) - new Date(v.time_in)) / 60000), 0) / ex.length);
  })();

  const TYPE_LABELS = { guest: 'Hotel Guest', visitor: 'Visitor', conference: 'Conference', delivery: 'Delivery/Supplier', staff: 'Staff', other: 'Other' };

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
        {[
          { label: 'Total Entries', value: visitors.length, icon: '📋', color },
          { label: 'Currently Inside', value: inside, icon: '🟢', color: '#22c55e' },
          { label: 'Exited',         value: exited,  icon: '✓', color: '#64748b' },
          { label: 'With Vehicles',  value: withCar, icon: '🚗', color: '#f59e0b' },
          { label: 'Avg Time on Premises', value: avgDur > 0 ? `${Math.floor(avgDur / 60)}h ${avgDur % 60}m` : '—', icon: '⏱️', color: '#8b5cf6' },
        ].map((k, i) => <KpiTile key={i} {...k} />)}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Card title="Visitor Types">
          {Object.entries(typeMap).map(([type, count]) => (
            <BarRow key={type} label={TYPE_LABELS[type] || type} value={count}
              max={Math.max(...Object.values(typeMap), 1)} color={color} />
          ))}
        </Card>

        <Card title="Currently Inside">
          {visitors.filter(v => v.status === 'in').length === 0 ? (
            <Empty text="No visitors currently on premises" />
          ) : (
            <Table
              headers={['Name', 'Type', 'Time In', 'Vehicle']}
              rows={visitors.filter(v => v.status === 'in').map(v => [
                <span style={{ fontWeight: 600 }}>{v.name}</span>,
                <span style={{ fontSize: 11, color: 'var(--textMuted)' }}>{TYPE_LABELS[v.visitor_type] || v.visitor_type}</span>,
                <span style={{ fontSize: 11 }}>{v.time_in ? new Date(v.time_in).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' }) : '—'}</span>,
                <span style={{ fontFamily: 'monospace', color: '#c45e3e', fontSize: 11, fontWeight: 700 }}>{v.car_plate || '—'}</span>,
              ])}
            />
          )}
        </Card>
      </div>

      <Card title={`Full Visitor Log (${visitors.length})`}>
        <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
          <Table
            headers={['Name', 'ID', 'Plate', 'Type', 'Time In', 'Time Out', 'Status']}
            rows={visitors.slice(0, 50).map(v => [
              <span style={{ fontWeight: 600 }}>{v.name}</span>,
              <span style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--textMuted)' }}>{v.id_number}</span>,
              <span style={{ fontFamily: 'monospace', color: '#c45e3e', fontSize: 11, letterSpacing: 1 }}>{v.car_plate || '—'}</span>,
              <span style={{ fontSize: 10, color: 'var(--textMuted)' }}>{TYPE_LABELS[v.visitor_type] || v.visitor_type}</span>,
              <span style={{ fontSize: 10 }}>{v.time_in ? new Date(v.time_in).toLocaleString('en-KE', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</span>,
              <span style={{ fontSize: 10, color: 'var(--textFaint)' }}>{v.time_out ? new Date(v.time_out).toLocaleString('en-KE', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</span>,
              <span style={{ fontSize: 10, fontWeight: 700, color: v.status === 'in' ? '#22c55e' : '#64748b' }}>{v.status === 'in' ? '🟢 Inside' : '✓ Exited'}</span>,
            ])}
          />
        </div>
      </Card>
    </div>
  );
}

function MaintenanceReport({ data, color }) {
  const wo      = data.work_orders || [];
  const summary = data.summary     || {};
  const assets  = data.assets      || [];

  const openWO     = wo.filter(w => w.status === 'open').length;
  const inProgWO   = wo.filter(w => w.status === 'in_progress').length;
  const closedWO   = wo.filter(w => w.status === 'closed').length;
  const urgentWO   = wo.filter(w => w.priority === 'urgent' || w.priority === 'critical').length;

  const STATUS_COLOR = { open: '#f59e0b', in_progress: '#3b82f6', closed: '#22c55e', cancelled: '#64748b' };
  const PRIO_COLOR   = { low: '#64748b', medium: '#f59e0b', high: '#e67e22', urgent: '#ef4444', critical: '#dc2626' };

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
        {[
          { label: 'Total WOs',     value: wo.length,                icon: '🔧', color },
          { label: 'Open',          value: openWO,                   icon: '🔴', color: '#f59e0b' },
          { label: 'In Progress',   value: inProgWO,                 icon: '🔵', color: '#3b82f6' },
          { label: 'Closed',        value: closedWO,                 icon: '✅', color: '#22c55e' },
          { label: 'Urgent/Critical',value: urgentWO,                icon: '⚠️', color: '#ef4444' },
          { label: 'Total Assets',  value: assets.length,            icon: '🏢', color: '#8b5cf6' },
          { label: 'Maint. Costs',  value: fmtKES(summary.total_cost || 0), icon: '💸', color: '#e67e22' },
        ].map((k, i) => <KpiTile key={i} {...k} />)}
      </div>

      {wo.length > 0 && (
        <Card title="Work Orders">
          <div style={{ overflowX: 'auto' }}>
            <Table
              headers={['WO#', 'Title', 'Location', 'Priority', 'Assigned', 'Status', 'Date']}
              rows={wo.slice(0, 20).map(w => [
                <span style={{ fontFamily: 'monospace', color: '#c8a96e', fontSize: 11 }}>#{w.id}</span>,
                <span style={{ fontWeight: 600, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', whiteSpace: 'nowrap' }}>{w.title}</span>,
                <span style={{ fontSize: 11, color: 'var(--textMuted)' }}>{w.location || '—'}</span>,
                <span style={{ fontSize: 10, fontWeight: 700, color: PRIO_COLOR[w.priority] || '#888', textTransform: 'uppercase' }}>{w.priority}</span>,
                <span style={{ fontSize: 11 }}>{w.assigned_to_name || '—'}</span>,
                <span style={{ fontSize: 10, fontWeight: 700, color: STATUS_COLOR[w.status] || '#888', textTransform: 'uppercase' }}>{w.status?.replace('_', ' ')}</span>,
                <span style={{ fontSize: 10, color: 'var(--textFaint)' }}>{new Date(w.created_at || Date.now()).toLocaleDateString('en-KE')}</span>,
              ])}
            />
          </div>
        </Card>
      )}

      {assets.length > 0 && (
        <Card title={`Asset Register (${assets.length} assets)`}>
          <Table
            headers={['Asset', 'Category', 'Location', 'Value', 'Condition']}
            rows={assets.slice(0, 15).map(a => [
              <span style={{ fontWeight: 600 }}>{a.name}</span>,
              <span style={{ fontSize: 11, color: 'var(--textMuted)' }}>{a.category || '—'}</span>,
              a.location || '—',
              <span style={{ fontWeight: 700, color }}>{fmtKES(a.current_value || a.purchase_price || 0)}</span>,
              <span style={{ fontSize: 10, fontWeight: 700, color: a.condition === 'good' ? '#22c55e' : a.condition === 'fair' ? '#f59e0b' : '#ef4444', textTransform: 'capitalize' }}>{a.condition || '—'}</span>,
            ])}
          />
        </Card>
      )}
    </div>
  );
}